import { useEffect, useRef } from 'react'
import { useParams } from 'react-router-dom'
import {
  usePostEndMutation,
  usePostPauseMutation,
  usePostStartMutation,
} from '@services/statistic'
import useMe from '@hooks/useMe'
import { log } from '@utils/console'
import {
  createStatsCommonForm,
  toEducationDestinationIri,
} from '@utils/courseThemeStats'

/**
 * Hook for handling PDF theme stats.
 * PDF themes are marked as completed immediately when opened.
 * Unlike SCORM, PDF doesn't send postMessage events, so we handle completion on mount.
 *
 * For free courses, the backend auto-creates a virtual assignment when educationId is missing.
 * The returned educationDestination IRI is used for subsequent stats calls.
 *
 * @param isFree - Whether the course is free (allows stats without educationId)
 */
const usePdfEventSender = (
  isFree: boolean | undefined,
  hasControlQuestion: boolean | undefined = undefined,
  isControlQuestionPassed = false,
  skipStats = false,
  isPdf = false
) => {
  const { themeId = '', educationId = '' } = useParams()
  const completedRef = useRef(false)
  const startedRef = useRef(false)
  // Track the actual educationDestination IRI (may be auto-created by backend)
  const educationDestinationRef = useRef<string | undefined>(undefined)

  const { isAdmin } = useMe()

  const [postStart] = usePostStartMutation()
  const [postEnd] = usePostEndMutation()
  const [postPause] = usePostPauseMutation()

  // Convert URL educationId to IRI
  const initialEducationDestinationIri = toEducationDestinationIri(educationId)

  // Mark PDF as completed immediately when opened, except for themes with control question.
  useEffect(() => {
    // Skip if already completed in this session
    if (completedRef.current) return

    // Авто-зачёт при открытии — только для PDF-тем (клиентские курсы).
    // SCORM-темы (в т.ч. все общие курсы) зачитываются по скрипту скорма
    // в useScormEventSender, а не при входе.
    if (!isPdf) return

    if (skipStats) {
      log('usePdfEventSender: skipping stats for preview')
      return
    }

    // Skip stats for admin users
    if (isAdmin) {
      log('usePdfEventSender: skipping stats for admin')
      return
    }

    // Skip if no themeId
    if (!themeId) return

    // For educationId=0, only record stats if course is free
    // (backend will auto-create virtual assignment for free courses)
    const shouldRecordStats = !!initialEducationDestinationIri || isFree === true
    if (!shouldRecordStats) {
      log('usePdfEventSender: skipping stats for non-free course without education')
      return
    }

    if (hasControlQuestion === undefined) {
      log('usePdfEventSender: control-question state unknown, waiting for data')
      return
    }

    const recordPdfCompletion = async () => {
      const commonForm = createStatsCommonForm(themeId, initialEducationDestinationIri)

      log('usePdfEventSender: recording PDF start', {
        ...commonForm,
        hasControlQuestion,
      })
      startedRef.current = true

      try {
        // Start the theme - backend may return auto-created educationDestination
        const startResponse = await postStart(commonForm).unwrap()

        // Store the actual educationDestination IRI (may be auto-created)
        const actualEducationDestination =
          startResponse.educationDestination || initialEducationDestinationIri
        educationDestinationRef.current = actualEducationDestination

        log('usePdfEventSender: recording PDF completion', {
          educationDestination: actualEducationDestination,
        })

        if (hasControlQuestion === true) {
          log('usePdfEventSender: control question present, skip auto-completion')

          return
        }

        // Immediately mark as completed (PDF completion = opening it)
        await postEnd({
          theme: '/api/course_chapter_themes/' + themeId,
          educationDestination: actualEducationDestination,
          testSuccess: true, // PDF themes don't have tests, always pass
        })

        completedRef.current = true
        startedRef.current = false

        log('usePdfEventSender: PDF marked as completed')
      } catch (error) {
        log('usePdfEventSender: error recording stats', error)
      }
    }

    void recordPdfCompletion()
  }, [
    themeId,
    educationId,
    isFree,
    isAdmin,
    postStart,
    postEnd,
    initialEducationDestinationIri,
    hasControlQuestion,
    skipStats,
    isPdf,
  ])

  // Return cleanup function for navigation
  return async () => {
    if (skipStats) {
      completedRef.current = false
      startedRef.current = false
      educationDestinationRef.current = undefined

      return
    }

    // Skip for admin
    if (isAdmin || isControlQuestionPassed) {
      if (isControlQuestionPassed) {
        completedRef.current = false
        startedRef.current = false
        educationDestinationRef.current = undefined
      }

      return
    }

    // If started but not completed (edge case: user navigates away quickly)
    if (startedRef.current && !completedRef.current && educationDestinationRef.current) {
      const commonForm = createStatsCommonForm(themeId, educationDestinationRef.current)
      log('usePdfEventSender: pausing on unmount', commonForm)
      await postPause(commonForm)
    }

    // Reset refs for next theme
    completedRef.current = false
    startedRef.current = false
    educationDestinationRef.current = undefined
  }
}

export default usePdfEventSender
